import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { KemCard } from '@/components/KemCard'
import { WidgetInterlaceCard } from '@/components/WidgetInterlaceCard'
import config from '@/config/env'
import { ActionsKemCard } from '../ActionsKemCard'
import { ActivityCashbackBanner } from '@/components/ActivityCashbackBanner'
import { LatestCardActivities } from '../LatestCardActivities'
import { CardInfoFromWallet } from '@/domain/wallet/walletTypes'
import { useKemCardStore } from '@/store/useKemCardStore'
import {
  useBlockKemCard,
  useGetWallet,
  useUnblockKemCard,
} from '@/domain/wallet/useCases'
import { kemCardStylesList } from '@/constants/kemCardStylesList'
import { responsiveAdapterVertical } from '@/utils'
import { Info, Lock, Unlock, WarningEllipses } from '@/assets/icons'
import { AppButton, Modal, PullToRefresh } from '@/components'
import { useTheme } from '@/hooks/useTheme'
import { toast } from 'react-hot-toast'
import { ActiveKemCardBgV2 } from '@/assets/images/backgrounds'

export interface WithKemCardProps {
  kemCardId?: string
  cardData?: CardInfoFromWallet
  isLoading?: boolean
}

export const WithKemCard: React.FC<WithKemCardProps> = ({
  kemCardId,
  cardData,
  isLoading,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const kemCardStyleKey = useKemCardStore((state) => state.kemCardStyleKey)
  const [isLockModalOpen, setIsLockModalOpen] = useState(false)

  const {
    data: walletData,
    isLoading: isWalletLoading,
    refetch: refetchWallet,
  } = useGetWallet()

  const [widgetFailed, setWidgetFailed] = useState(false)
  const isInterlace = cardData?.card_provider === 'interlace' && !!config.INTERLACE_WIDGET_URL && !widgetFailed

  const usdtCurrency = walletData?.completeCurrencies?.find(
    (currency) => currency.name === 'USDT',
  )

  const { mutateAsync: blockKemCard, isPending: isLocking } = useBlockKemCard()
  const { mutateAsync: unblockKemCard, isPending: isUnlocking } =
    useUnblockKemCard()

  const selectedKemCardStyle = kemCardStylesList.find(
    (item) => item.key === kemCardStyleKey,
  )
  const isLocked = cardData && cardData.state !== 'active'
  const isLoadingBlockAndUnblock =
    isLocking || isUnlocking || isWalletLoading || isLoading

  const handleLockCard = async () => {
    if (!kemCardId) return
    try {
      await blockKemCard({ card_id: kemCardId })
      setIsLockModalOpen(false)
    } catch (error) {
      console.error('Error locking card:', error)
      toast.error('Error locking card')
    }
  }

  const openLockModal = () => {
    setIsLockModalOpen(true)
  }

  const handleUnlockCard = async () => {
    if (!kemCardId) return
    try {
      await unblockKemCard({ card_id: kemCardId })
    } catch (error) {
      console.error('Error unlocking card:', error)
      toast.error('Error unlocking card')
    }
  }

  const getBalanceFontSize = (balanceText: string) => {
    const fontSizeSnapPoints = [
      { maxLength: 9, fontSize: 40 },
      { maxLength: 11, fontSize: 30 },
      { maxLength: 14, fontSize: 28 },
      { maxLength: Infinity, fontSize: 20 }, // For anything above 14
    ]

    // Find the first snap point where the text length is less than or equal to the limit
    const fontSize =
      fontSizeSnapPoints.find(
        ({ maxLength }) => balanceText.length <= maxLength,
      )?.fontSize ?? 20 // Fallback
    return fontSize
  }
  const balanceFontSize = getBalanceFontSize(
    usdtCurrency?.formattedFiatBalance || '',
  )
  const redirectToKemCardInfoScreen = () => {
    navigate('/kem-card/card-info')
  }

  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)
    try {
      await refetchWallet()
    } finally {
      setIsRefreshing(false)
    }
  }, [refetchWallet])

  const handlePullRefresh = React.useCallback(async () => {
    await handleRefresh()
  }, [handleRefresh])

  return (
    <BaseView
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100vh',
      }}
    >
      {/* Background Image - fixed position */}
      <BaseView
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: '100vh',
          backgroundImage: `url(${ActiveKemCardBgV2})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <PullToRefresh
        onRefresh={handlePullRefresh}
        isRefreshing={isRefreshing}
        debug={process.env.NODE_ENV === 'development'}
        style={{
          position: 'relative',
          zIndex: 1,
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            flex: 1,
            paddingLeft: '13px',
            paddingRight: '13px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            paddingBottom: '16px',
            minHeight: '100vh',
          }}
        >
          <BaseView py={20} gap={24} style={{ paddingBottom: 60 }}>
            {/* Header */}
            <BaseView
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <BaseView
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <BaseView style={{ gap: responsiveAdapterVertical(28) }}>
                  <BaseText
                    fontSize={24}
                    fontFamily="bold"
                    color="label.default"
                  >
                    Kem Card
                  </BaseText>
                  <BaseView>
                    <BaseText
                      fontSize={balanceFontSize}
                      fontFamily="semibold"
                      color="label.default"
                    >
                      {usdtCurrency?.formattedFiatBalance}
                    </BaseText>
                    <BaseText
                      fontSize={16}
                      fontFamily="regular"
                      color="label.description"
                    >
                      {t('translation:kemCard.cardBalance')}
                    </BaseText>
                  </BaseView>
                </BaseView>
                <BaseView
                  onClick={redirectToKemCardInfoScreen}
                  style={{
                    cursor: 'pointer',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    alignSelf: 'flex-start',
                    zIndex: 100,
                    position: 'relative',
                  }}
                >
                  {isLocked && (
                    <BaseView
                      px={8}
                      py={4}
                      rounded={8}
                      style={{ backgroundColor: '#ef4444' }}
                    >
                      <BaseText fontSize={12} fontFamily="medium" color="white">
                        {t('translation:locked')}
                      </BaseText>
                    </BaseView>
                  )}
                  <Info />
                </BaseView>
              </BaseView>
            </BaseView>

            {/* Card Visual Component — Interlace widget or static card */}
            <BaseView style={{ alignItems: 'center' }}>
              {isInterlace && kemCardId ? (
                <WidgetInterlaceCard
                  kemCardId={kemCardId}
                  kemCardEnabled={!isLocked}
                  onError={() => setWidgetFailed(true)}
                />
              ) : (
                selectedKemCardStyle && (
                  <KemCard
                    kemCardStyleItem={selectedKemCardStyle}
                    cardInfo={cardData}
                    kemCardId={kemCardId}
                    kemCardEnabled={!isLocked}
                    skeletonActive={isLoading}
                  />
                )
              )}
            </BaseView>

            {/* Cashback Banner - shows activity cashback info */}

            {/* Card Actions Grid */}
            <ActionsKemCard
              isLocked={isLocked}
              lockCard={openLockModal}
              unlockCard={handleUnlockCard}
              isLoading={isLoading}
              isLoadingBlockAndUnblock={isLoadingBlockAndUnblock}
            />
            <ActivityCashbackBanner />

            {/* Latest Card Activities */}
            <LatestCardActivities />

            <BaseView gap={12}>
              <BaseText
                fontSize={18}
                fontFamily="semibold"
                color="label.default"
              >
                {t('screens:security.title')}
              </BaseText>
              <BaseView
                bgColor="background.cardItemRoot"
                rounded={24}
                p={12}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (isLocked) {
                    handleUnlockCard()
                  } else {
                    openLockModal()
                  }
                }}
              >
                <BaseView
                  style={{
                    width: 44,
                    height: 44,
                    backgroundColor: '#454647',
                    borderRadius: 22,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!isLoadingBlockAndUnblock &&
                    (isLocked ? <Unlock size={24} /> : <Lock size={24} />)}
                  {isLoadingBlockAndUnblock && (
                    <BaseView
                      style={{
                        width: '24px',
                        height: '24px',
                        border: '2px solid transparent',
                        borderTop: `2px solid ${theme.colors.label.default}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                  )}
                </BaseView>
                <BaseView>
                  <BaseText
                    fontSize={16}
                    fontFamily="medium"
                    color="label.default"
                  >
                    {isLocked
                      ? t('translation:kemCard.unlockCardAction')
                      : t('translation:kemCard.lockCardAction')}
                  </BaseText>
                  <BaseText
                    fontSize={12}
                    fontFamily="regular"
                    color="label.description"
                  >
                    {isLocked
                      ? t('translation:kemCard.unlockCardActionDescription')
                      : t('translation:kemCard.lockCardActionDescription')}
                  </BaseText>
                </BaseView>
              </BaseView>
            </BaseView>
          </BaseView>
          <Modal
            isOpen={isLockModalOpen}
            onClose={() => setIsLockModalOpen(false)}
          >
            <BaseView
              style={{ alignItems: 'center', justifyContent: 'center' }}
            >
              <WarningEllipses size={110} />
              <BaseText fontSize={28} textAlign="center" fontFamily="semibold">
                {t('screens:cards.lockCard')}
              </BaseText>
              <BaseText
                fontSize={16}
                textAlign="center"
                fontFamily="regular"
                color="label.description"
                lineHeight={20}
                // style={{ marginBottom: responsiveAdapterVertical(16) }}
              >
                {t('screens:cards.lockCardDescription')}
              </BaseText>
              <BaseView
                mt={40}
                gap={12}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <AppButton
                  label={t('screens:cards.lockCardCancel')}
                  type="secondaryGray"
                  onPress={() => {
                    setIsLockModalOpen(false)
                  }}
                />
                <AppButton
                  label={t('screens:cards.lockCardConfirm')}
                  type="warning"
                  onPress={() => {
                    handleLockCard()
                  }}
                  isLoading={isLoadingBlockAndUnblock}
                />
              </BaseView>
            </BaseView>
          </Modal>
        </div>
      </PullToRefresh>
    </BaseView>
  )
}
